// src/Contact.js

import React, { useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SubmitButton from './SubmitButton';

const ACCESS_KEY = "69ed1e6d-8ab4-459b-b462-03cf01932860";

// ── Static animation configs ──────────────────────────────────────────────────
const sectionAnim    = { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true, amount: 0.1 }, transition: { duration: 0.5 } };
const labelAnim      = { initial: { y: -16, opacity: 0 }, whileInView: { y: 0, opacity: 1 }, viewport: { once: true }, transition: { delay: 0.15, duration: 0.4 } };
const headingAnim    = { initial: { y: -16, opacity: 0 }, whileInView: { y: 0, opacity: 1 }, viewport: { once: true }, transition: { delay: 0.2,  duration: 0.4 } };
const underlineAnim  = { initial: { width: 0 }, whileInView: { width: 64 }, viewport: { once: true }, transition: { duration: 0.5, delay: 0.35 } };
const subtextAnim    = { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true }, transition: { delay: 0.25, duration: 0.4 } };
const cardAnim       = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { delay: 0.3, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } };
const fieldAnim      = (delay) => ({ initial: { opacity: 0, y: 12 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.4, delay } });
const statusAnim     = { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -6 } };

// ── QR grid — reduced to 32 cells, CSS animation instead of framer ────────────
const QrGridBackground = memo(() => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 overflow-hidden opacity-[0.03] dark:opacity-[0.05]"
  >
    <div
      className="absolute inset-0 grid grid-cols-8"
      style={{ gridTemplateRows: 'repeat(4, 1fr)' }}
    >
      {Array.from({ length: 32 }).map((_, i) => (
        <div
          key={i}
          className="border border-purple-500"
          style={{
            opacity: 0,
            animation: "qrCellPulse " + (3 + (i % 4)) + "s ease-in-out " + ((i * 0.08) % 3) + "s infinite",
          }}
        />
      ))}
    </div>
  </div>
));
QrGridBackground.displayName = 'QrGridBackground';

// ── Particles — CSS animation instead of framer ───────────────────────────────
const PARTICLES = [
  { width: 8,  height: 8,  top: "8%",  left: "4%",  duration: 4,   delay: 0   },
  { width: 10, height: 10, top: "18%", left: "90%", duration: 5,   delay: 0.6 },
  { width: 6,  height: 6,  top: "60%", left: "5%",  duration: 3.5, delay: 1   },
  { width: 8,  height: 8,  top: "88%", left: "12%", duration: 5.5, delay: 2   },
];

const ParticlesLayer = memo(() => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0">
    {PARTICLES.map((p, i) => (
      <div
        key={i}
        className="absolute rounded-full bg-purple-400/15 dark:bg-purple-400/10"
        style={{
          width:     p.width,
          height:    p.height,
          top:       p.top,
          left:      p.left,
          animation: "particleFloat " + p.duration + "s ease-in-out " + p.delay + "s infinite",
        }}
      />
    ))}
  </div>
));
ParticlesLayer.displayName = 'ParticlesLayer';

// ── Glow orbs — static, no animation ─────────────────────────────────────────
const GlowOrbs = memo(() => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
    <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-purple-500/8 dark:bg-purple-500/10 blur-[120px] hw-accel" />
    <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-indigo-500/8 dark:bg-indigo-500/10 blur-[100px] hw-accel" />
  </div>
));
GlowOrbs.displayName = 'GlowOrbs';

// ── Animated field wrapper ────────────────────────────────────────────────────
const AnimatedField = memo(({ children, delay }) => (
  <motion.div {...fieldAnim(delay)}>
    {children}
  </motion.div>
));
AnimatedField.displayName = 'AnimatedField';

// ── Input class helper ────────────────────────────────────────────────────────
const INPUT_BASE    = "w-full p-4 outline-none border-2 rounded-xl bg-gray-50 dark:bg-[#0f0f1a] dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-colors duration-200 text-gray-900";
const INPUT_IDLE    = " border-gray-200 dark:border-purple-500/20";
const INPUT_FOCUSED = " border-purple-500 dark:border-purple-500 ring-2 ring-purple-500/20";

// ── Main component ────────────────────────────────────────────────────────────
const Contact = memo(() => {
  const [formState, setFormState] = useState({ status: 'idle', message: '' });
  const [focused,   setFocused]   = useState('');

  const onSubmit = useCallback(async (event) => {
    event.preventDefault();

    setFormState({ status: 'sending', message: 'Sending your message...' });

    const formData = new FormData(event.target);
    formData.append("access_key", ACCESS_KEY);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body:   formData,
      });
      const data = await response.json();

      if (data.success) {
        setFormState({ status: 'success', message: "Message sent! I'll get back to you soon." });
        event.target.reset();
        setTimeout(() => setFormState({ status: 'idle', message: '' }), 5000);
      } else {
        setFormState({ status: 'error', message: data.message || "Something went wrong. Please try again." });
      }
    } catch {
      setFormState({ status: 'error', message: "Failed to send. Please check your connection." });
    }
  }, []);

  const handleFocus   = useCallback((name) => () => setFocused(name), []);
  const handleBlur    = useCallback(() => setFocused(''), []);
  const getInputClass = useCallback((name) => INPUT_BASE + (focused === name ? INPUT_FOCUSED : INPUT_IDLE), [focused]);

  return (
    <motion.section
      id="contact"
      aria-labelledby="contact-heading"
      {...sectionAnim}
      className="relative w-full px-6 md:px-12 lg:px-24 py-24 bg-gray-50 dark:bg-[#0f0f1a] transition-colors duration-500 overflow-hidden"
    >
      <QrGridBackground />
      <ParticlesLayer />
      <GlowOrbs />

      <div className="relative z-10 max-w-2xl mx-auto">

        <motion.p
          {...labelAnim}
          className="text-center mb-2 text-sm font-semibold uppercase tracking-widest text-purple-500 dark:text-purple-400"
        >
          Contact
        </motion.p>

        <motion.h2
          id="contact-heading"
          {...headingAnim}
          className="text-center text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight"
        >
          Get in Touch
        </motion.h2>

        <div className="flex justify-center mb-4">
          <motion.div
            {...underlineAnim}
            className="h-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
          />
        </div>

        <motion.p
          {...subtextAnim}
          className="text-center text-gray-500 dark:text-gray-400 mb-12 text-base max-w-xl mx-auto leading-relaxed"
        >
          Have a question, feedback, or just want to say hello? Fill out the form
          below and I'll get back to you as soon as possible.
        </motion.p>

        <motion.div
          {...cardAnim}
          className="bg-white dark:bg-[#1a1a2e] border border-gray-100 dark:border-purple-500/10 rounded-2xl p-8 shadow-lg dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
        >
          <form onSubmit={onSubmit} className="space-y-5" noValidate>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <AnimatedField delay={0.4}>
                <div className="space-y-1">
                  <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name <span className="text-purple-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    placeholder="Your name"
                    required
                    name="name"
                    autoComplete="name"
                    onFocus={handleFocus('name')}
                    onBlur={handleBlur}
                    className={getInputClass('name')}
                  />
                </div>
              </AnimatedField>

              <AnimatedField delay={0.45}>
                <div className="space-y-1">
                  <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email <span className="text-purple-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    placeholder="your@email.com"
                    required
                    name="email"
                    autoComplete="email"
                    onFocus={handleFocus('email')}
                    onBlur={handleBlur}
                    className={getInputClass('email')}
                  />
                </div>
              </AnimatedField>
            </div>

            <AnimatedField delay={0.5}>
              <div className="space-y-1">
                <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Message <span className="text-purple-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  rows="6"
                  placeholder="What's on your mind?"
                  required
                  name="message"
                  onFocus={handleFocus('message')}
                  onBlur={handleBlur}
                  className={getInputClass('message') + " resize-none"}
                />
              </div>
            </AnimatedField>

            <AnimatedField delay={0.55}>
              <SubmitButton status={formState.status} />
            </AnimatedField>

            <AnimatePresence mode="wait">
              {formState.message && (
                <motion.p
                  key={formState.status}
                  {...statusAnim}
                  role="alert"
                  aria-live="polite"
                  className={
                    "text-center text-sm font-medium py-3 px-4 rounded-xl " +
                    (formState.status === 'success'
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                      : formState.status === 'error'
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                      : 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400')
                  }
                >
                  {formState.message}
                </motion.p>
              )}
            </AnimatePresence>

          </form>
        </motion.div>
      </div>
    </motion.section>
  );
});

Contact.displayName = 'Contact';
export default Contact;